/**
 * Activity Feed — Unified activity stream
 *
 * Aggregates all user-relevant events into a single chronological feed:
 * - Atom published (by followed users or in followed topics)
 * - Discussion replies in topics user follows
 * - Resonances on user's atoms
 * - New topic discoveries (recommended)
 * - System events (weekly review, level-up)
 *
 * Replaces scattered notification hooks with a coherent timeline.
 */

// ── Types ──

export type ActivityType =
  | 'atom_published'
  | 'atom_resonated'
  | 'discussion_reply'
  | 'topic_new_atom'
  | 'follow_new_atom'
  | 'weekly_review'
  | 'level_up'
  | 'daily_collision'
  | 'badge_earned';

export interface ActivityItem {
  id: string;
  type: ActivityType;
  /** Primary message */
  title: string;
  /** Secondary detail */
  description: string;
  /** Who triggered this activity */
  actorId: string | null;
  actorName: string | null;
  /** Target entity */
  targetId: string | null;
  targetType: 'atom' | 'topic' | 'discussion' | 'user' | null;
  /** ISO timestamp */
  timestamp: string;
  /** Whether user has seen this */
  read: boolean;
  /** Optional action URL */
  actionUrl: string | null;
  /** Visual category for icon/color */
  category: 'content' | 'social' | 'system' | 'achievement';
}

export interface ActivityFeed {
  items: ActivityItem[];
  unreadCount: number;
  lastReadAt: string | null;
  hasMore: boolean;
}

export interface ActivityFeedConfig {
  /** Max items in feed */
  maxItems: number;
  /** How old items can be (days) */
  maxAgeDays: number;
  /** Group similar items within N minutes */
  groupWindowMinutes: number;
}

// ── Constants ──

export const DEFAULT_FEED_CONFIG: ActivityFeedConfig = {
  maxItems: 50,
  maxAgeDays: 30,
  groupWindowMinutes: 30,
};

const STORAGE_KEY = 'ideagraph_activity_feed';
// const READ_KEY = 'ideagraph_activity_last_read';

// ── Activity Creation ──

/**
 * Create an activity item for when an atom is published.
 */
export function createAtomPublishedActivity(
  atomId: string,
  atomTitle: string,
  authorId: string,
  authorName: string,
  _topicId?: string
): ActivityItem {
  return {
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'atom_published',
    title: `${authorName} 分享了一个新想法`,
    description: atomTitle,
    actorId: authorId,
    actorName: authorName,
    targetId: atomId,
    targetType: 'atom',
    timestamp: new Date().toISOString(),
    read: false,
    actionUrl: `/idea/${atomId}`,
    category: 'content',
  };
}

/**
 * Create activity for a resonance on user's atom.
 */
export function createResonanceActivity(
  atomId: string,
  atomTitle: string,
  resonatorId: string,
  resonatorName: string
): ActivityItem {
  return {
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'atom_resonated',
    title: `${resonatorName} 对你的想法产生了共鸣`,
    description: atomTitle,
    actorId: resonatorId,
    actorName: resonatorName,
    targetId: atomId,
    targetType: 'atom',
    timestamp: new Date().toISOString(),
    read: false,
    actionUrl: `/idea/${atomId}`,
    category: 'social',
  };
}

/**
 * Create activity for a discussion reply.
 */
export function createDiscussionReplyActivity(
  topicId: string,
  topicTitle: string,
  _replyId: string,
  authorId: string,
  authorName: string
): ActivityItem {
  return {
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'discussion_reply',
    title: `${authorName} 在「${topicTitle}」中回复了讨论`,
    description: '',
    actorId: authorId,
    actorName: authorName,
    targetId: topicId,
    targetType: 'topic',
    timestamp: new Date().toISOString(),
    read: false,
    actionUrl: `/topic/${topicId}`,
    category: 'social',
  };
}

/**
 * Create activity for level-up achievement.
 */
export function createLevelUpActivity(
  userId: string,
  newLevel: number,
  levelName: string
): ActivityItem {
  return {
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'level_up',
    title: `你升级为「${levelName}」了！`,
    description: `参与等级 ${newLevel}`,
    actorId: null,
    actorName: null,
    targetId: userId,
    targetType: 'user',
    timestamp: new Date().toISOString(),
    read: false,
    actionUrl: '/profile/me',
    category: 'achievement',
  };
}

/**
 * Create activity for badge earned.
 */
export function createBadgeActivity(
  userId: string,
  badgeName: string,
  badgeDescription: string
): ActivityItem {
  return {
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'badge_earned',
    title: `获得徽章「${badgeName}」`,
    description: badgeDescription,
    actorId: null,
    actorName: null,
    targetId: userId,
    targetType: 'user',
    timestamp: new Date().toISOString(),
    read: false,
    actionUrl: '/profile/me',
    category: 'achievement',
  };
}

/**
 * Create activity for daily collision availability.
 */
export function createDailyCollisionActivity(
  collisionId: string,
  hook: string
): ActivityItem {
  return {
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'daily_collision',
    title: '今日碰撞',
    description: hook,
    actorId: null,
    actorName: null,
    targetId: collisionId,
    targetType: null,
    timestamp: new Date().toISOString(),
    read: false,
    actionUrl: '/',
    category: 'content',
  };
}

/**
 * Create activity for weekly review availability.
 */
export function createWeeklyReviewActivity(
  userId: string,
  weekLabel: string,
  insightPreview: string
): ActivityItem {
  return {
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type: 'weekly_review',
    title: `${weekLabel}思考回顾`,
    description: insightPreview,
    actorId: null,
    actorName: null,
    targetId: userId,
    targetType: 'user',
    timestamp: new Date().toISOString(),
    read: false,
    actionUrl: '/profile/me',
    category: 'system',
  };
}

// ── Feed Management ──

/**
 * Build a feed from raw activity items.
 */
export function buildFeed(
  items: ActivityItem[],
  config: Partial<ActivityFeedConfig> = {}
): ActivityFeed {
  const cfg = { ...DEFAULT_FEED_CONFIG, ...config };
  const now = Date.now();
  const maxAge = cfg.maxAgeDays * 24 * 60 * 60 * 1000;

  // Filter by age
  const fresh = items.filter((item) => {
    const age = now - new Date(item.timestamp).getTime();
    return age <= maxAge;
  });

  // Sort by timestamp descending
  const sorted = [...fresh].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  // Group similar items within time window
  const grouped = groupSimilarItems(sorted, cfg.groupWindowMinutes);

  // Take top N
  const trimmed = grouped.slice(0, cfg.maxItems);
  const unreadCount = trimmed.filter((i) => !i.read).length;

  return {
    items: trimmed,
    unreadCount,
    lastReadAt: null,
    hasMore: grouped.length > cfg.maxItems,
  };
}

/**
 * Mark all items as read up to now.
 */
export function markAllRead(feed: ActivityFeed): ActivityFeed {
  return {
    ...feed,
    items: feed.items.map((i) => ({ ...i, read: true })),
    unreadCount: 0,
    lastReadAt: new Date().toISOString(),
  };
}

/**
 * Mark a single item as read.
 */
export function markItemRead(feed: ActivityFeed, itemId: string): ActivityFeed {
  const items = feed.items.map((i) =>
    i.id === itemId ? { ...i, read: true } : i
  );
  return {
    ...feed,
    items,
    unreadCount: items.filter((i) => !i.read).length,
  };
}

/**
 * Add new items to an existing feed.
 */
export function appendToFeed(
  feed: ActivityFeed,
  newItems: ActivityItem[],
  config: Partial<ActivityFeedConfig> = {}
): ActivityFeed {
  const allItems = [...newItems, ...feed.items];
  return buildFeed(allItems, config);
}

// ── Grouping ──

/**
 * Group similar activities within a time window.
 * E.g., "3 people resonated with your atom" instead of 3 separate items.
 */
function groupSimilarItems(
  items: ActivityItem[],
  windowMinutes: number
): ActivityItem[] {
  if (items.length === 0) return [];

  const windowMs = windowMinutes * 60 * 1000;
  const result: ActivityItem[] = [];
  const consumed = new Set<number>();

  for (let i = 0; i < items.length; i++) {
    if (consumed.has(i)) continue;

    const item = items[i];
    const itemTime = new Date(item.timestamp).getTime();

    // Find similar items within window
    let groupCount = 1;
    for (let j = i + 1; j < items.length; j++) {
      if (consumed.has(j)) continue;
      const other = items[j];
      const otherTime = new Date(other.timestamp).getTime();

      if (
        item.type === other.type &&
        item.targetId === other.targetId &&
        Math.abs(itemTime - otherTime) < windowMs
      ) {
        groupCount++;
        consumed.add(j);
      }
    }

    if (groupCount > 1) {
      // Create grouped item
      result.push({
        ...item,
        title: groupTitle(item, groupCount),
      });
    } else {
      result.push(item);
    }
  }

  return result;
}

function groupTitle(item: ActivityItem, count: number): string {
  switch (item.type) {
    case 'atom_resonated':
      return `${count} 人对你的想法产生了共鸣`;
    case 'discussion_reply':
      return `${count} 条新回复`;
    case 'atom_published':
      return `${count} 个新想法被分享`;
    default:
      return item.title;
  }
}

// ── Persistence ──

export function saveFeed(feed: ActivityFeed): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(feed));
  } catch { /* storage unavailable */ }
}

export function loadFeed(): ActivityFeed | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Validate minimal schema to guard against corrupt/outdated data
    if (!Array.isArray(parsed?.items) || typeof parsed?.unreadCount !== 'number') return null;
    return parsed as ActivityFeed;
  } catch {
    return null;
  }
}

// ── Filtering ──

/**
 * Filter feed by category.
 */
export function filterByCategory(
  feed: ActivityFeed,
  category: ActivityItem['category']
): ActivityItem[] {
  return feed.items.filter((i) => i.category === category);
}

/**
 * Get only unread items.
 */
export function getUnreadItems(feed: ActivityFeed): ActivityItem[] {
  return feed.items.filter((i) => !i.read);
}
